import React, { useEffect, useMemo, useState } from 'react'
import { Field } from 'formik'
import pluralize from 'pluralize'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, startCase } from 'lodash'
import { IconAlertTriangle, IconAlertCircle, IconFlask } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Pill } from '@/components/pills/Pill'
import { Card } from '@/components/cards/Card'
import {
  Checkbox,
  MultiSelect,
  NumberField,
  RadioGroup,
  Select as FormikSelect,
  TextArea,
  TextField,
} from '@/components/forms/formik'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import {
  fetchActiveVoipCampaignsByOrg,
  fetchAgentsByOrg,
  fetchOrganizationsMappedVoipCampaigns,
  fetchDispositionsByOrg,
  fetchPlaybooksByOrg,
  fetchTagsByOrg,
} from '@/reducers/qa-copilot/qa-copilot.actions'
import {
  createPromptTest,
  deletePromptTest,
  getPromptTestResults,
} from '@/reducers/prompts/prompts.actions'
import { LlmModelSelect } from '@/components/forms/formik/selects/LlmModelSelect'
import { getSelectableModelOptions } from '@/components/forms/formik/selects/modelGating'
import { ModelDeprecatedNotice } from '@/components/forms/formik/selects/ModelDeprecatedNotice'
import { QACopilotScoringStrategyInputs } from '@/views/QACopilot/components/QACopilotScoringStrategyInputs'
import { TimestampSilenceFields } from '@/views/QACopilot/components/TimestampSilenceFields'
import { Banner } from '@/components/banners/Banner'
import { CHANNEL_TYPE } from '@/utils/constants'
import { getChannelTypeOptions, hasAnyOmnichannelAccess } from '@/utils/helpers'
import Select from '@/components/forms/Select'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'

export function getInactiveSelectedVoipCampaigns(activeVoipCampaigns, selectedVoipCampaigns) {
  // Normalize label: lowercase, replace underscores and spaces with single space, trim
  const normalize = (label) => {
    if (!label) {
      return ''
    }
    return label
      .toLowerCase()
      .replace(/[_\s]+/g, ' ')
      .trim()
  }

  const activeLabels = activeVoipCampaigns?.map((campaign) => normalize(campaign?.label)) || []

  return (
    selectedVoipCampaigns?.filter(
      (selectedCampaign) => !activeLabels.includes(normalize(selectedCampaign.label))
    ) || []
  )
}

const QACopilotSettingsFormComponent = ({
  values,
  errors,
  handleDeleteScorecardConfig,
  isBaltoAdmin,
  isPromptEditor,
  eligibilityBlocked,
  fallbackBlocked,
  isCreate,
  setFieldValue,
  isOutdatedScorecard,
}) => {
  const dispatch = useDispatch()
  const {
    loading: { promptTests: promptTestsLoading },
    prompts,
    modelOptions: {
      qa_copilot: qaCopilotModelOptions = [],
      copilot_fallback: copilotFallbackModelOptions = [],
    } = {},
  } = useSelector((state) => state.prompts)
  const organizations = useSelector((state) => state.organizations)
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)
  const { data, loading } = useSelector((state) => state.qaCopilot)
  const {
    loading: { modelOptions: modelOptionsLoading },
  } = useSelector((state) => state.prompts)
  const [copilotTestResponse, setCopilotTestResponse] = useState(null)
  const isPromptTestingDisabled = !values.prompt_id
  const isPromptPairEmpty = isEmpty(values.call_id) || isEmpty(values.expected_response)
  const prompt = prompts.find((prompt) => values.prompt_id && prompt.id === values.prompt_id)
  const promptTests = prompt?.prompt_tests || []

  const { organizationid: currentUserOrg, hierarchy_manager } = useSelector(
    (state) => state.currentUser
  )
  const validOrgId = values.organization_id !== '' && values.organization_id !== 0
  const showManagerHierarchyOrgDropdown = !isBaltoAdmin && hierarchy_manager
  const omnichannelEnabled = useMemo(() => {
    if (isBaltoAdmin) {
      const scorecardConfigOrganization = organizations.find(
        (org) => org.id === values.organization_id
      )
      return hasAnyOmnichannelAccess(scorecardConfigOrganization?.active_channels)
    }
    return hasAnyOmnichannelAccess(activeChannels)
  }, [values.organization_id])

  const multiCallScoringEnabled = useMemo(() => {
    const scorecardConfigOrganization = organizations.find(
      (org) => org.id === values.organization_id
    )
    return scorecardConfigOrganization?.multi_call_scoring_enabled
  }, [values.organization_id, organizations])

  const dispositionsVisible = !(
    showManagerHierarchyOrgDropdown &&
    values.organization_id !== currentUserOrg &&
    validOrgId
  )

  const fetchAllFilters = () => {
    dispatch(fetchOrganizationsMappedVoipCampaigns(values.organization_id))
    dispatch(fetchActiveVoipCampaignsByOrg(values.organization_id))
    dispatch(fetchAgentsByOrg(values.organization_id))
    dispatch(fetchTagsByOrg(values.organization_id))
    dispatch(fetchPlaybooksByOrg(values.organization_id))
    if (dispositionsVisible) {
      dispatch(fetchDispositionsByOrg(values.organization_id))
    }
  }

  const handleSavePromptTest = async () => {
    dispatch(
      createPromptTest(prompt, {
        inputs: { call_id: values.call_id, call_type: values.call_type || CHANNEL_TYPE.CALL },
        expected_response: values.expected_response,
        prompt_uuid: prompt.prompt_uuid,
      })
    )

    setFieldValue('call_id', '')
    setFieldValue('expected_response', '')
  }

  const handleDeletePromptTest = (promptTestId) => {
    dispatch(deletePromptTest(prompts, prompt, promptTestId))
  }

  const handleTestCopilotPrompt = async () => {
    const response = await dispatch(
      getPromptTestResults(values.prompt_id, {
        user_prompt: values.user_prompt,
        llm_model_id: values.llm_model_id,
        feature: 'qa_copilot_test',
        system_prompt: values.system_prompt,
        first_x_minutes: values.first_x_minutes || null,
        last_x_minutes: values.last_x_minutes || null,
        side: values.side,
        include_timestamps: values.include_timestamps === 'true',
        silence_threshold_seconds: values.silence_threshold_seconds || null,
        max_response_tokens: values.max_response_tokens,
        scores_to_fallback: values.scores_to_fallback?.map((score) => score.value),
        fallback_initial_model_id: values.fallback_initial_model_id,
      })
    )

    setCopilotTestResponse(response)
  }

  useEffect(() => {
    // fetch the updated data when the dropdown changes
    if (isCreate && isBaltoAdmin && values.organization_id) {
      fetchAllFilters()
    }
  }, [values.organization_id])

  useEffect(() => {
    // fetch the defaults on load
    if (values.organization_id) {
      fetchAllFilters()
    }
  }, [])

  const inactiveSelectedVoipCampaigns = useMemo(() => {
    return getInactiveSelectedVoipCampaigns(data.activeVoipCampaigns, values.voip_campaigns)
  }, [data.activeVoipCampaigns, values.voip_campaigns])

  const voipInactiveWarningMessage = !isEmpty(inactiveSelectedVoipCampaigns)
    ? {
        text: `There have been no matching calls for the following campaigns within the last 60 days: 
                ${inactiveSelectedVoipCampaigns.map((campaign) => campaign.label).join(', ')}.`,
      }
    : null

  const wrongMappingVoipCampaigns = useMemo(() => {
    const selectedCampaignIds = new Set(values.voip_campaigns?.map((campaign) => campaign.value))
    const selectedPlaybookCids = new Set(values.playbooks?.map((playbook) => playbook.value))

    return (
      data.allVoipCampaigns?.filter(
        (campaign) =>
          selectedCampaignIds.has(campaign.id) &&
          !isEmpty(values.playbooks) &&
          !selectedPlaybookCids.has(campaign.config_cid)
      ) || []
    )
  }, [values.voip_campaigns, values.playbooks])

  const voipWrongMappingWarningMessage = !isEmpty(wrongMappingVoipCampaigns)
    ? {
        text: `${wrongMappingVoipCampaigns.length} of your selected VoIP campaigns are mapped to a different Playbook than the ones you have selected above.
                 This setup may result in no calls getting scored. Affected campaigns: ${wrongMappingVoipCampaigns.map((campaign) => ` ${campaign.name}`)}.`,
      }
    : null

  const call_type = values.call_type || CHANNEL_TYPE.CALL
  const idToUse = call_type === CHANNEL_TYPE.CALL ? 'Call_ID' : 'Message_ID'

  const generalEligibilitySettings = (
    <>
      <div className="non-repeater-grid">
        <div className="label-style">Agents</div>
        <Field
          name="users_inclusive"
          options={[
            { value: 'true', label: 'Includes' },
            { value: 'false', label: 'Excludes' },
          ]}
          component={FormikSelect}
          isDisabled={isOutdatedScorecard}
          isClearable={false}
        />
        <Field
          name="users"
          options={data.agents}
          component={MultiSelect}
          loading={loading.agents}
          condenseOptions={false}
          showFullOptions
          placeholder="Select agents (optional)"
          style={{ flex: 1 }}
          isDisabled={isOutdatedScorecard}
        />
      </div>
      <Pill small emphasized>
        AND
      </Pill>
      <div className="non-repeater-grid">
        <div className="label-style">Tags</div>
        <Field
          name="tags_inclusive"
          options={[
            { value: 'true', label: 'Includes' },
            { value: 'false', label: 'Excludes' },
          ]}
          component={FormikSelect}
          isDisabled={isOutdatedScorecard}
          isClearable={false}
        />
        <Field
          name="tags"
          options={data.tags}
          component={MultiSelect}
          loading={loading.tags}
          condenseOptions={false}
          showFullOptions
          placeholder="Select tags (optional)"
          style={{ flex: 1 }}
          isDisabled={isOutdatedScorecard}
        />
      </div>
    </>
  )

  const emailEligibilitySettings = (
    <>
      {generalEligibilitySettings}
      <Pill small emphasized>
        AND
      </Pill>
      <div className="non-repeater-grid">
        <div className="label-style" data-testid="min-message-count">
          Message Count
        </div>
        <div className="muted-text">is at least</div>
        <div className="flex-align-center gap" style={{ flex: 1 }}>
          <Field
            name="min_message_count"
            className="qa-copilot-settings-form-min-max-duration-minutes"
            component={NumberField}
            placeholder="Set minimum message count (Optional)"
            disabled={isOutdatedScorecard}
          />
          <div className="muted-text">{pluralize('message', values.min_message_count)}</div>
        </div>
      </div>
      <Pill small emphasized>
        AND
      </Pill>
      <div className="non-repeater-grid">
        <div className="label-style" data-testid="max-message-count">
          Message Count
        </div>
        <div className="muted-text">is at most</div>
        <div className="flex-align-center gap" style={{ flex: 1 }}>
          <Field
            name="max_message_count"
            className="qa-copilot-settings-form-min-max-duration-minutes"
            component={NumberField}
            placeholder="Set maximum message count (Optional)"
            disabled={isOutdatedScorecard}
          />
          <div className="muted-text">{pluralize('message', values.max_message_count)}</div>
        </div>
      </div>
    </>
  )

  const callEligibilitySettings = (
    <>
      <div className="non-repeater-grid">
        <div className="label-style">Call Length</div>
        <div className="muted-text">is at least</div>
        <div className="flex-align-center gap" style={{ flex: 1 }}>
          <Field
            name="min_duration_in_minutes"
            className="qa-copilot-settings-form-min-max-duration-minutes"
            component={NumberField}
            placeholder="Set minimum call duration in minutes"
            disabled={isOutdatedScorecard}
          />
          <div className="muted-text">{pluralize('minute', values.min_duration_in_minutes)}</div>
        </div>
      </div>
      <Pill small emphasized>
        AND
      </Pill>
      <div className="non-repeater-grid">
        <div className="label-style">Call Length</div>
        <div className="muted-text">is at most</div>
        <div className="flex-align-center gap" style={{ flex: 1 }}>
          <Field
            name="max_duration_in_minutes"
            className="qa-copilot-settings-form-min-max-duration-minutes"
            component={NumberField}
            placeholder="Set maximum call duration in minutes (optional)"
            disabled={isOutdatedScorecard}
          />
          <div className="muted-text">{pluralize('minute', values.max_duration_in_minutes)}</div>
        </div>
      </div>
      <Pill small emphasized>
        AND
      </Pill>
      {generalEligibilitySettings}
      <Pill small emphasized>
        AND
      </Pill>
      <div className="non-repeater-grid">
        <div className="label-style">Playbook</div>
        <div className="muted-text">matches</div>
        <Field
          name="playbooks"
          options={data.playbooks}
          component={MultiSelect}
          loading={loading.playbooks}
          condenseOptions={false}
          showFullOptions
          placeholder="Select Playbooks (optional)"
          style={{ flex: 1 }}
          isDisabled={isOutdatedScorecard}
        />
      </div>
      {dispositionsVisible && !isEmpty(data.dispositions) && (
        <>
          <Pill small emphasized>
            AND
          </Pill>
          <div className="non-repeater-grid">
            <div className="label-style">Dispositions</div>
            <Field
              name="dispositions_inclusive"
              options={[
                { value: 'true', label: 'Includes' },
                { value: 'false', label: 'Excludes' },
              ]}
              component={FormikSelect}
              isDisabled={isOutdatedScorecard}
              isClearable={false}
            />
            <Field
              name="dispositions"
              options={data.dispositions}
              component={MultiSelect}
              loading={loading.dispositions}
              condenseOptions={false}
              showFullOptions
              placeholder="Select Dispositions (optional)"
              style={{ flex: 1 }}
              isDisabled={isOutdatedScorecard}
            />
          </div>
        </>
      )}
      <Pill small emphasized>
        AND
      </Pill>
      <div className="non-repeater-grid">
        <div className="label-style">VoIP Campaigns</div>
        <Field
          name="voip_campaigns_inclusive"
          options={[
            { value: 'true', label: 'Includes' },
            { value: 'false', label: 'Excludes' },
          ]}
          component={FormikSelect}
          isDisabled={isOutdatedScorecard}
          isClearable={false}
        />
        <Field
          name="voip_campaigns"
          options={data.allVoipCampaigns?.map((campaign) => ({
            label: startCase(campaign.name),
            value: campaign.id,
          }))}
          component={MultiSelect}
          loading={loading.allVoipCampaigns}
          condenseOptions={false}
          showFullOptions
          placeholder="Select VoIP Campaigns (optional)"
          style={{ flex: 1 }}
          isDisabled={isOutdatedScorecard}
        />
      </div>
      {voipInactiveWarningMessage && (
        <Banner
          shrunk
          shrunkHeight
          caution
          dataTestId="voip-inactive-campaign-message"
          icon={<IconAlertTriangle />}
        >
          {voipInactiveWarningMessage.text}
        </Banner>
      )}
      {voipWrongMappingWarningMessage && (
        <Banner
          shrunk
          shrunkHeight
          caution
          dataTestId="voip-wrong-mapping-message"
          icon={<IconAlertTriangle />}
        >
          {voipWrongMappingWarningMessage.text}
        </Banner>
      )}
    </>
  )

  return (
    <>
      {isOutdatedScorecard && (
        <Banner
          dataTestId="qa-copilot-settings-form-read-only-banner"
          header="This scorecard is read-only."
          informative
          icon={<IconAlertCircle />}
          shrunk
          shrunkHeight
          style={{ marginBottom: 24 }}
        >
          <p>
            You&apos;re viewing the settings for this version. Edits aren&apos;t allowed in
            read-only mode.
          </p>
        </Banner>
      )}
      <div>
        {isBaltoAdmin && (
          <div data-testid="organization-dropdown">
            <FormField required data-testid="organization_id-select">
              <label htmlFor="organization_id">Organization</label>
              <Select
                name="organization_id"
                value={values.organization_id}
                onChange={(selectedOption) =>
                  setFieldValue('organization_id', selectedOption.value)
                }
                options={organizations.map((org) => ({ value: org.id, label: org.name }))}
                isClearable={false}
                isDisabled={!isCreate}
              />
            </FormField>
            {!isCreate && (
              <p className="muted-text">
                Clone scorecard to change the organization on an existing scorecard.
              </p>
            )}
          </div>
        )}
        {omnichannelEnabled && (
          <div data-testid="callType-radio-group-wrapper" className="call-type-radio-group">
            <Field
              required
              name="call_type"
              label="Channel"
              data-testid="callType-radio-group"
              component={RadioGroup}
              disabled={isOutdatedScorecard}
              options={getChannelTypeOptions(activeChannels)}
            />
          </div>
        )}
        <h3>Scoring Threshold</h3>
        <p className="muted-text">
          Set performance bands for which {pluralize(call_type)} are exceeding expectations, meeting
          expectations, or needing improvements.
        </p>
        <div className="multiple-field-container horizontal">
          <Field
            required
            name="exceeds_threshold"
            label="Exceeds (%)"
            component={NumberField}
            disabled={isOutdatedScorecard}
          />
          <Field
            required
            name="meets_threshold"
            label="Meets (%)"
            component={NumberField}
            disabled={isOutdatedScorecard}
          />
          <Field
            required
            name="improvement_threshold"
            label="Below (%)"
            component={NumberField}
            disabled={isOutdatedScorecard}
          />
        </div>
        {call_type === CHANNEL_TYPE.CALL && multiCallScoringEnabled && (
          <>
            <div className="flex-align-center flex-space-between mt">
              <h3>Multi-Call Scoring</h3>
              <Field
                data-testid="multicall-toggle"
                name="multicall"
                toggle
                component={Checkbox}
                disabled={isOutdatedScorecard}
              />
            </div>
            <p className="muted-text">
              Combine multiple calls from the same customer into one QA score. When enabled, calls
              that share the same VoIP customer ID are grouped and evaluated together instead of
              individually.
            </p>
            {values.multicall && (
              <FormField data-testid="multicall_same_user-dropdown">
                <label>Multi-Call Grouping</label>
                <Select
                  options={[
                    {
                      value: false,
                      label: 'All agents',
                      description: 'Combine calls from any agent who spoke with the same customer.',
                    },
                    {
                      value: true,
                      label: 'Single agent only',
                      description: 'Only combine calls handled by the same agent.',
                    },
                  ]}
                  value={values.multicall_same_user}
                  onChange={(selectedOption) =>
                    setFieldValue('multicall_same_user', selectedOption.value)
                  }
                  placeholder="Select grouping strategy"
                  isClearable={false}
                  isDisabled={isOutdatedScorecard}
                />
              </FormField>
            )}
          </>
        )}
        <h3 data-testid="eligibility-prompt-header">Eligibility Prompt</h3>
        <p className="muted-text">
          The following reasoning will be used to determine if a {call_type} is eligible for QA
          scoring.
        </p>
        <Field
          name="user_prompt"
          component={TextArea}
          rows={10}
          disabled={isOutdatedScorecard}
          readOnly={!isPromptEditor}
        />
        {isPromptEditor && (
          <>
            <div className="copilot-prompt__testing-headline">
              <IconFlask className="icon-svg" />
              <h3>Testing</h3>
            </div>
            <Card>
              <div className="copilot-prompt__additional-details">
                <div>
                  <div>{startCase(call_type)} IDs</div>
                  <div className="flex-align-center small-gap">
                    <Field
                      name="call_id"
                      component={TextField}
                      placeholder={`Enter a ${idToUse}`}
                      className="call-id-field"
                      disabled={isOutdatedScorecard}
                    />
                    <div>.....</div>
                    <Field
                      name="expected_response"
                      component={TextField}
                      placeholder="Expected response"
                      disabled={isOutdatedScorecard}
                    />
                    <Button
                      brand
                      onClick={handleSavePromptTest}
                      disabled={isPromptTestingDisabled || isPromptPairEmpty || isOutdatedScorecard}
                      disabledTooltip={
                        isPromptPairEmpty
                          ? `You must add a ${idToUse} and an expected response`
                          : 'You must save the scorecard with an eligibility prompt before setting up prompt testing'
                      }
                    >
                      Add Pair
                    </Button>
                  </div>
                </div>
                <div>
                  {promptTests.map((promptTest) => (
                    <div key={promptTest.id} className="prompt-test flex-align-center small-gap">
                      <Field
                        name={`call_id_${promptTest.inputs.call_id}`}
                        component={TextField}
                        placeholder={promptTest.inputs.call_id}
                        disabled
                      />
                      <div>.....</div>
                      <Field
                        name={`expected_response_${promptTest.id}`}
                        component={TextField}
                        disabled
                        placeholder={promptTest.expected_response}
                      />

                      <Button
                        critical
                        onClick={() => handleDeletePromptTest(promptTest.id)}
                        style={{ marginTop: '0.5rem' }}
                        disabled={isOutdatedScorecard}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handleTestCopilotPrompt}
                  style={{ marginTop: '0.5rem' }}
                  disabled={isEmpty(promptTests) || isOutdatedScorecard}
                  disabledTooltip="Create a test first"
                  type="button"
                >
                  Run Test
                </Button>
                <div>
                  {promptTestsLoading ? (
                    <div className="empty-copilot-response">
                      <Loader />
                    </div>
                  ) : (
                    copilotTestResponse && (
                      <div className="copilot-response-holder">
                        {copilotTestResponse.map((response) => (
                          <div className="copilot-response" key={response.prompt_test_id}>
                            <p>
                              <strong>Inputs: </strong>
                              <span>{JSON.stringify(response.inputs)}</span>
                            </p>
                            <p>
                              <strong>Expected Response: </strong>{' '}
                              <span>{response.expected_response}</span>
                            </p>
                            <p>
                              <strong>Actual Response: </strong>{' '}
                              <span>{response.actual_response}</span>
                            </p>
                            <p>
                              <strong>Tokens Used: </strong>
                              <span>{response.tokens_used}</span>
                            </p>
                            {response.fallback_used !== null && (
                              <p>
                                <strong>Fallback Used: </strong>{' '}
                                <span>{response.fallback_used ? 'True' : 'False'}</span>
                              </p>
                            )}
                            <p>
                              <strong>Model Used: </strong>
                              <span>{response.executed_model}</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              </div>
            </Card>
            <div className="copilot-prompt__testing-headline">
              <IconFlask className="icon-svg" />
              <h3>Eligibility Prompt Settings</h3>
            </div>
            <Card>
              <div className="copilot-prompt__additional-details">
                <div className="flex-align-center small-gap additional-details-holder">
                  <div className="label-style">Expected Response</div>
                  <div className="expected-response-holder">
                    <Field
                      name="should_eligibility_respond_yes"
                      component={RadioGroup}
                      options={[
                        { value: 'true', text: 'Yes' },
                        { value: 'false', text: 'No' },
                      ]}
                      disabled={isOutdatedScorecard}
                    />
                    {errors.should_eligibility_respond_yes && (
                      <ErrorMessage content={errors.should_eligibility_respond_yes} />
                    )}
                  </div>
                </div>
              </div>
              <div className="copilot-prompt__additional-details">
                <div className="flex-align-center small-gap additional-details-holder">
                  <div className="label-style">Fallback Strategy</div>
                  <div className="flex-column medium-gap flex-align-end">
                    <div className="copilot-prompt__fallback-line">
                      <span>Fallback if score is</span>
                      <Field
                        name="scores_to_fallback"
                        component={MultiSelect}
                        condenseOptions={false}
                        showFullOptions
                        placeholder="Any of Yes/No/NA"
                        options={[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' },
                          { value: 'n/a', label: 'N/A' },
                        ]}
                        isDisabled={isOutdatedScorecard}
                      />
                    </div>
                    {!isEmpty(values.scores_to_fallback) && (
                      <div className="copilot-prompt__fallback-line">
                        <span>Run</span>
                        <Field
                          name="fallback_initial_model_id"
                          component={LlmModelSelect}
                          options={getSelectableModelOptions(copilotFallbackModelOptions, {
                            isNew: isCreate,
                          })}
                          loading={modelOptionsLoading}
                          disabled={isOutdatedScorecard}
                        />
                        <span>first</span>
                      </div>
                    )}
                    {/* Computed by the modal, which owns the save the block protects. */}
                    <ModelDeprecatedNotice show={fallbackBlocked} />
                  </div>
                </div>
              </div>
              {call_type === CHANNEL_TYPE.CALL && (
                <div className="copilot-prompt__additional-details">
                  <div className="flex-align-center small-gap additional-details-holder">
                    <div className="label-style">Use Specific Timeframes</div>
                    <div className="flex-align-center small-gap">
                      <Field
                        placeholder="Minutes at the start"
                        name="first_x_minutes"
                        component={NumberField}
                        disabled={isOutdatedScorecard}
                      />
                      <Field
                        placeholder="Minutes at the end"
                        name="last_x_minutes"
                        component={NumberField}
                        disabled={isOutdatedScorecard}
                      />
                    </div>
                  </div>
                </div>
              )}
              {call_type === CHANNEL_TYPE.CALL && (
                <TimestampSilenceFields disabled={isOutdatedScorecard} />
              )}
              <div className="copilot-prompt__additional-details">
                <div className="flex-align-center small-gap additional-details-holder">
                  <div className="label-style">Side to include in transcript</div>
                  <div data-testid="side-select">
                    <Select
                      name="side"
                      value={values.side}
                      onChange={(selectedOption) => setFieldValue('side', selectedOption.value)}
                      options={[
                        { value: 'both', label: 'Both' },
                        { value: 'agent', label: 'Agent Only' },
                        { value: 'customer', label: 'Customer Only' },
                      ]}
                      isDisabled={isOutdatedScorecard}
                      isClearable={false}
                    />
                  </div>
                </div>
              </div>
              <div className="copilot-prompt__additional-details">
                <div className="flex-align-center small-gap additional-details-holder">
                  <div className="label-style">AI Model</div>
                  <div>
                    <Field
                      name="llm_model_id"
                      component={LlmModelSelect}
                      options={getSelectableModelOptions(qaCopilotModelOptions, {
                        isNew: isCreate,
                      })}
                      loading={modelOptionsLoading}
                      disabled={isOutdatedScorecard}
                    />
                    {errors.llm_model_id && <ErrorMessage content={errors.llm_model_id} />}
                    {/* Computed by the modal, which owns the save the block protects. */}
                    <ModelDeprecatedNotice show={eligibilityBlocked} />
                  </div>
                </div>
              </div>
            </Card>

            <h3>Shell Prompt</h3>
            <p className="muted-text">
              Pre-fills the prompt of any criterion that doesn&apos;t have one yet: AI criteria, and
              manual criteria with Timestamp Marker set to Yes. Use it for context every criterion
              needs, such as what the organization does and how its calls are scored. Each criterion
              keeps its own copy, so editing this doesn&apos;t change existing criteria.
            </p>
            <Field
              name="shell_prompt"
              component={TextArea}
              rows={10}
              disabled={isOutdatedScorecard}
            />
          </>
        )}

        <h3>Scoring Goal {!isBaltoAdmin && ' (Read Only)'}</h3>
        <p className="muted-text">
          Set how many {pluralize(call_type)} you&apos;d like to score each{' '}
          {values.scorecard_frequency === 'weekly' ? 'week' : 'month'} to meet your QA goals.
        </p>
        <QACopilotScoringStrategyInputs
          isOutdatedScorecard={isOutdatedScorecard}
          callType={call_type}
        />

        <h3>Manual Scoring Goal</h3>
        <p className="muted-text">
          If you use manual criteria, enter how many {pluralize(call_type)} you&apos;d like to score
          manually each {values.scorecard_frequency === 'weekly' ? 'week' : 'month'}. We&apos;ll
          create one task per {call_type}, up to this number. Leave this blank to create tasks for
          all scorecards with manual criteria.
        </p>

        <div className="multiple-field-container horizontal">
          <Field
            name="required_manual_task_count"
            component={NumberField}
            disabled={isOutdatedScorecard}
          />
          <span>
            {pluralize(call_type)} per {values.scorecard_frequency === 'weekly' ? 'week' : 'month'}
          </span>
        </div>

        <div className="flex-align-center flex-space-between mt">
          <h3>Ignore Partially Scored</h3>
          <Field
            data-testid="ignore-scored-status-toggle"
            name="ignore_scored_status"
            toggle
            component={Checkbox}
            disabled={isOutdatedScorecard}
          />
        </div>
        <p className="muted-text">
          When this is on, {pluralize(call_type)} with missing manual criteria will still count as
          fully scored. These missing criteria will be marked as N/A, not incomplete.
        </p>

        <h2>{startCase(pluralize(call_type))} Eligibility Settings</h2>
        <h3>Qualifying {startCase(pluralize(call_type))}</h3>
        <p className="muted-text">
          {startCase(pluralize(call_type))} that meet the criteria specified in the query below will
          qualify for scoring. Note that a single {call_type} may be assessed by several scorecards
          concurrently.
        </p>
        <div className="multiple-field-container">
          {call_type === CHANNEL_TYPE.CALL
            ? callEligibilitySettings
            : call_type === CHANNEL_TYPE.EMAIL ||
                call_type === CHANNEL_TYPE.CHAT ||
                call_type === CHANNEL_TYPE.SMS
              ? emailEligibilitySettings
              : ''}
        </div>
        <h2>Danger Zone</h2>
        <div className="flex-align-center flex-space-between gap">
          <div>
            <p className="label-style">Delete this Scorecard</p>
            <p className="muted-text">
              Please be aware this action is irreversible. Removing the scorecard will also
              permanently erase all associated evaluations.
            </p>
          </div>
          <Button critical onClick={handleDeleteScorecardConfig} disabled={isOutdatedScorecard}>
            Delete Scorecard
          </Button>
        </div>
      </div>
    </>
  )
}
export const QACopilotSettingsForm = QACopilotSettingsFormComponent
